import { fmtDate, fmtTime } from '../../utils/datetime';
import React, { useEffect, useState } from "react";
import {
    Building2, Calendar, ChevronDown, ClipboardList,
    Eye, MapPin, Search, Stethoscope, TrendingUp, Users, X,
} from "lucide-react";
import PageLayout from "../../components/PageLayout";
import { useAuth } from "../../context/AuthContext";
import { getAllEmployees } from "../../api/employee";
import { getCallReport, getCallReportVisitDetails } from "../../api/report";
import config from "../../config";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Employee { id: number; name: string; employee_code?: string; }

interface Visit {
    visit_id: number;
    visit_type: "doctor" | "firm";
    name: string;
    code: string | null;
    employee_name: string;
    employee_code: string | null;
    designation: string | null;
    visit_date: string;
    visit_from: string | null;
    visit_to: string | null;
    call_type: string | null;
    status: "closed" | "open" | string;
    remarks: string | null;
    manually_closed?: boolean;
    address?: string | null;
    city?: string | null;
}

interface VisitDetail {
    visit_id: number;
    visit_type: string;
    employee_name: string;
    employee_code: string | null;
    employee_designation: string | null;
    client_name: string;
    client_code: string | null;
    address: string | null;
    city: string | null;
    visit_date: string | null;
    last_visit_date: string | null;
    check_in_time: string | null;
    check_out_time: string | null;
    call_objective: string | null;
    call_objective_remark: string | null;
    working_with: string[];
    client_feedback: string | null;
    prescribing_products: string[];
    top_5_products: string[];
    status: string | null;
    manually_closed: boolean;
    latitude: number | null;
    longitude: number | null;
}

interface CallReportData {
    total_visits: number;
    closed_visits: number;
    open_visits: number;
    doctor_visits: number;
    firm_visits: number;
    visits: Visit[];
}

interface Props { onLogout: () => void; onNavigate: (page: string) => void; }

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split("T")[0];
const weekAgo = () => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0];
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color }: { icon: React.ElementType; label: string; value: number; color: string }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
        </div>
    );
}

function Badge({ status, manuallyClosed }: { status: string; manuallyClosed?: boolean }) {
    const cls = status === "closed"
        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
        : "bg-amber-50 text-amber-700 border border-amber-200";
    return (
        <div className="flex items-center gap-1.5">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status}</span>
            {manuallyClosed && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-300">Manual</span>
            )}
        </div>
    );
}

function TypeBadge({ type }: { type: string }) {
    return type === "doctor"
        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"><Stethoscope className="w-3 h-3" />Doctor</span>
        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"><Building2 className="w-3 h-3" />Firm</span>;
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

const MAPS_KEY = config.googleMapsApiKey;

function Chip({ label, color }: { label: string; color: string }) {
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>{label}</span>;
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-xs text-gray-500 w-36 shrink-0 font-medium pt-0.5">{label}</span>
            <span className="text-sm text-gray-800 flex-1">{value}</span>
        </div>
    );
}

function DetailModal({ detail, onClose }: { detail: VisitDetail | null; onClose: () => void }) {
    if (!detail) return null;

    const isDoctor = detail.visit_type === "doctor";
    const hasMap = detail.latitude != null && detail.longitude != null;
    const mapUrl = hasMap
        ? `https://maps.googleapis.com/maps/api/staticmap?center=${detail.latitude},${detail.longitude}&zoom=15&size=560x180&markers=color:red%7C${detail.latitude},${detail.longitude}&key=${MAPS_KEY}`
        : null;
    const mapsLink = hasMap
        ? `https://maps.google.com/?q=${detail.latitude},${detail.longitude}`
        : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r ${isDoctor ? "from-blue-600 to-blue-500" : "from-purple-600 to-purple-500"}`}>
                    <div>
                        <div className="flex items-center gap-2 mb-0.5">
                            {isDoctor
                                ? <Stethoscope className="w-4 h-4 text-white/80" />
                                : <Building2 className="w-4 h-4 text-white/80" />}
                            <h2 className="font-bold text-white text-lg leading-tight">{detail.client_name}</h2>
                            {detail.client_code && <span className="text-white/60 text-xs">({detail.client_code})</span>}
                        </div>
                        <p className="text-white/70 text-xs">{detail.employee_name}{detail.employee_designation ? ` · ${detail.employee_designation}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {detail.status && (
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${detail.status === "closed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                {detail.status}
                            </span>
                        )}
                        {detail.manually_closed && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">Manual</span>
                        )}
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors ml-1">
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    {/* 2-col: Employee + Timing */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Employee</p>
                            <Row label="Name" value={detail.employee_name} />
                            {detail.employee_code && <Row label="Code" value={detail.employee_code} />}
                            {detail.employee_designation && <Row label="Designation" value={detail.employee_designation} />}
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Timing</p>
                            <Row label="Visit Date" value={detail.visit_date ?? "—"} />
                            <Row label="Check In" value={detail.check_in_time ?? "—"} />
                            <Row label="Check Out" value={detail.check_out_time ?? "—"} />
                            {detail.last_visit_date && <Row label="Last Visit" value={detail.last_visit_date} />}
                        </div>
                    </div>

                    {/* Location */}
                    {(detail.address || detail.city) && (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Location</p>
                            {detail.city && <Row label="City" value={detail.city} />}
                            {detail.address && <Row label="Address" value={detail.address} />}
                        </div>
                    )}

                    {/* Call Details */}
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Call Details</p>
                        <Row label="Objective" value={detail.call_objective ?? "—"} />
                        {detail.call_objective_remark && <Row label="Remark" value={detail.call_objective_remark} />}
                        {detail.client_feedback && <Row label="Feedback" value={detail.client_feedback} />}
                    </div>

                    {/* Working With */}
                    {detail.working_with && detail.working_with.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Working With</p>
                            <div className="flex flex-wrap gap-1.5">
                                {detail.working_with.map((name, i) => (
                                    <Chip key={i} label={name} color="bg-blue-50 text-blue-700 border-blue-200" />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Medicines (doctor only) */}
                    {isDoctor && (
                        <>
                            {detail.top_5_products && detail.top_5_products.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Promoted Products</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detail.top_5_products.map((m, i) => (
                                            <Chip key={i} label={m} color="bg-teal-50 text-teal-700 border-teal-200" />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {detail.prescribing_products && detail.prescribing_products.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Prescribing Products</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detail.prescribing_products.map((m, i) => (
                                            <Chip key={i} label={m} color="bg-emerald-50 text-emerald-700 border-emerald-200" />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* GPS Map */}
                    {hasMap && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Visit Location (GPS)</p>
                                <a href={mapsLink!} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                                    <MapPin className="w-3 h-3" />Open in Maps
                                </a>
                            </div>
                            {MAPS_KEY ? (
                                <img
                                    src={mapUrl!}
                                    alt="Visit location"
                                    className="w-full rounded-xl border border-gray-200 object-cover"
                                    style={{ height: 180 }}
                                />
                            ) : (
                                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl text-sm text-gray-600 border border-gray-200">
                                    <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                                    <span>{detail.latitude?.toFixed(6)}, {detail.longitude?.toFixed(6)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function CallReportsPage({ onLogout, onNavigate }: Props) {
    const { user } = useAuth();
    const tenantId = (user as any)?.tenant_id as number;

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectedEmp, setSelectedEmp] = useState<number | null>(null);
    const [fromDate, setFromDate] = useState(weekAgo());
    const [toDate, setToDate] = useState(today());
    const [data, setData] = useState<CallReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<"all" | "doctor" | "firm">("all");
    const [statusFilter, setStatusFilter] = useState<"all" | "closed" | "open">("all");
    const [detail, setDetail] = useState<VisitDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState<number | null>(null);

    useEffect(() => {
        if (!tenantId) return;
        getAllEmployees(tenantId).then((list: any[]) =>
            setEmployees((Array.isArray(list) ? list : []).map(e => ({
                id: e.id,
                name: e.user?.name ?? e.name ?? e.employee_name ?? "",
                employee_code: e.employee_code ?? e.code,
            })))
        ).catch(() => {});
    }, [tenantId]);

    const fetchReport = async (empId?: number | null) => {
        if (!tenantId) return;
        setLoading(true);
        try {
            const res = await getCallReport(tenantId, fromDate, toDate, empId !== undefined ? empId : selectedEmp);
            const raw = res.data.data ?? res.data;
            const doctorVisits: Visit[] = raw.doctor_visits ?? [];
            const firmVisits: Visit[] = raw.firm_visits ?? [];
            const summary = raw.summary ?? {};
            setData({
                total_visits: (summary.total_doctor_visits ?? 0) + (summary.total_firm_visits ?? 0),
                closed_visits: (summary.closed_doctor_visits ?? 0) + (summary.closed_firm_visits ?? 0),
                open_visits: (summary.open_doctor_visits ?? 0) + (summary.open_firm_visits ?? 0),
                doctor_visits: summary.total_doctor_visits ?? 0,
                firm_visits: summary.total_firm_visits ?? 0,
                visits: [...doctorVisits, ...firmVisits],
            });
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // auto-load all employees' data when page first opens
    useEffect(() => { if (tenantId) fetchReport(null); }, [tenantId]);

    const openDetail = async (visit: Visit) => {
        setDetailLoading(visit.visit_id);
        try {
            const res = await getCallReportVisitDetails(tenantId, visit.visit_id, visit.visit_type);
            setDetail(res.data.data ?? res.data);
        } catch {
            setDetail({
                visit_id: visit.visit_id,
                visit_type: visit.visit_type,
                employee_name: visit.employee_name,
                employee_code: visit.employee_code,
                employee_designation: visit.designation,
                client_name: visit.name,
                client_code: visit.code,
                address: visit.address ?? null,
                city: visit.city ?? null,
                visit_date: visit.visit_date,
                last_visit_date: null,
                check_in_time: visit.visit_from,
                check_out_time: visit.visit_to,
                call_objective: visit.call_type,
                call_objective_remark: null,
                working_with: [],
                client_feedback: visit.remarks,
                prescribing_products: [],
                top_5_products: [],
                status: visit.status,
                manually_closed: visit.manually_closed ?? false,
                latitude: null,
                longitude: null,
            });
        } finally {
            setDetailLoading(null);
        }
    };

    const visits = data?.visits ?? [];
    const filtered = visits.filter(v => {
        const matchSearch = !search || (v.name ?? '').toLowerCase().includes(search.toLowerCase()) || (v.employee_name ?? '').toLowerCase().includes(search.toLowerCase());
        const matchType = typeFilter === "all" || v.visit_type === typeFilter;
        const matchStatus = statusFilter === "all" || v.status === statusFilter;
        return matchSearch && matchType && matchStatus;
    });

    return (
        <PageLayout onLogout={onLogout} currentPage="reports/call-reports" onNavigate={onNavigate}>
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Call Reports</h1>
                            <p className="text-xs text-gray-500">Track all doctor & firm visits by your team</p>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {/* Filters */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                                <div className="relative">
                                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <select value={selectedEmp ?? ""} onChange={e => setSelectedEmp(e.target.value ? Number(e.target.value) : null)}
                                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white">
                                        <option value="">All Employees</option>
                                        {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <button onClick={() => fetchReport()} disabled={loading}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors">
                                {loading ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                                {loading ? "Loading…" : "Search"}
                            </button>
                        </div>
                    </div>

                    {data && (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                                <StatCard icon={ClipboardList} label="Total Visits" value={data.total_visits} color="bg-blue-500" />
                                <StatCard icon={TrendingUp} label="Closed Visits" value={data.closed_visits} color="bg-emerald-500" />
                                <StatCard icon={Calendar} label="Open Visits" value={data.open_visits} color="bg-amber-500" />
                                <StatCard icon={Stethoscope} label="Doctor Visits" value={data.doctor_visits} color="bg-indigo-500" />
                                <StatCard icon={Building2} label="Firm Visits" value={data.firm_visits} color="bg-purple-500" />
                            </div>

                            {/* Table Filters */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
                                    <div className="relative flex-1 min-w-48">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        <input type="text" placeholder="Search by name or employee…" value={search} onChange={e => setSearch(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                    </div>
                                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)}
                                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                        <option value="all">All Types</option>
                                        <option value="doctor">Doctor</option>
                                        <option value="firm">Firm</option>
                                    </select>
                                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}
                                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                                        <option value="all">All Status</option>
                                        <option value="closed">Closed</option>
                                        <option value="open">Open</option>
                                    </select>
                                    <span className="text-xs text-gray-500 font-medium">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
                                </div>

                                {/* Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-100">
                                                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Check In</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Check Out</th>
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                                                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Details</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {filtered.length === 0 ? (
                                                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No records found</td></tr>
                                            ) : filtered.map(v => (
                                                <tr key={`${v.visit_type}-${v.visit_id}`} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-5 py-3.5">
                                                        <div className="flex items-center gap-2">
                                                            <TypeBadge type={v.visit_type} />
                                                            <span className="font-medium text-gray-900">{v.name}</span>
                                                        </div>
                                                        {v.code && <span className="text-xs text-gray-400 ml-0.5">{v.code}</span>}
                                                    </td>
                                                    <td className="px-4 py-3.5">
                                                        <span className="text-gray-700">{v.employee_name}</span>
                                                        {v.employee_code && <span className="block text-xs text-gray-400">{v.employee_code}</span>}
                                                        {v.designation && <span className="block text-xs text-gray-400">{v.designation}</span>}
                                                    </td>
                                                    <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{fmtDate(v.visit_date)}</td>
                                                    <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{fmtTime(v.visit_from)}</td>
                                                    <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{fmtTime(v.visit_to)}</td>
                                                    <td className="px-4 py-3.5"><Badge status={v.status} manuallyClosed={v.manually_closed} /></td>
                                                    <td className="px-4 py-3.5 text-center">
                                                        <button onClick={() => openDetail(v)} disabled={detailLoading === v.visit_id}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-60 transition-colors">
                                                            {detailLoading === v.visit_id
                                                                ? <span className="w-3 h-3 border border-blue-400 border-t-blue-600 rounded-full animate-spin" />
                                                                : <Eye className="w-3.5 h-3.5" />}
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {!data && !loading && (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <ClipboardList className="w-12 h-12 mb-3 opacity-30" />
                            <p className="text-sm">Select filters and click Search to load call reports</p>
                        </div>
                    )}
                </div>
            <DetailModal detail={detail} onClose={() => setDetail(null)} />
        </PageLayout>
    );
}
